import React, { useEffect } from 'react';
import Layout from "../temp/layout/Layout";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { 
    FaSchool, FaUniversity, FaChalkboardTeacher, FaGraduationCap,
    FaCheckCircle, FaArrowRight, FaRocket
} from 'react-icons/fa';

const Solutions = () => {
    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const solutions = [
        {
            icon: <FaSchool className="text-6xl" />,
            title: "Smart School Suite",
            description: "Complete digital solution designed for modern schools to simplify administration, enhance student engagement, and optimize daily academic operations.",
            features: [
                "Student & Teacher Management",
                "Attendance & Timetable",
                "Fee & Exam Management",
                "Parent-Teacher Communication",
                "Report Card Generation"
            ],
            color: "from-blue-500 to-indigo-600"
        },
        {
            icon: <FaUniversity className="text-6xl" />,
            title: "Enterprise Suite for Big Schools",
            description: "High-performance platform built specifically for large schools, offering advanced management tools, seamless communication, and scalable systems.",
            features: [
                "Multi-campus Management",
                "Advanced Analytics",
                "Scalable Infrastructure",
                "Custom Workflows",
                "Enterprise Security"
            ],
            color: "from-purple-500 to-pink-600"
        },
        {
            icon: <FaGraduationCap className="text-6xl" />,
            title: "Institutional & College Master Suite",
            description: "Enterprise-grade solution crafted for colleges and educational institutions, streamlining campus-wide administration and academic delivery.",
            features: [
                "Course & Credit Management",
                "Semester System",
                "Research Tracking",
                "Alumni Management",
                "Advanced Reporting"
            ],
            color: "from-green-500 to-teal-600"
        },
        {
            icon: <FaChalkboardTeacher className="text-6xl" />,
            title: "Coaching & Training Academy Suite",
            description: "Specialized platform tailored for coaching centers and training academies, empowering them to manage batches, schedules, and performance.",
            features: [
                "Batch Management",
                "Performance Tracking",
                "Mock Test System",
                "Progress Analytics",
                "Student Engagement"
            ],
            color: "from-orange-500 to-red-600"
        }
    ];

    return (
        <Layout>
            <Helmet>
                <title>Solutions - Educational Eternity | Education ERP Solutions</title>
                <meta name="description" content="Comprehensive education ERP solutions for schools, colleges, and coaching institutes. Choose the perfect suite for your institution." />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[50vh] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `url(assets/images/home1.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
                <div className="relative z-10 text-center px-4">
                    <h1 data-aos="fade-up" className="text-5xl md:text-7xl font-bold text-white mb-4">
                        Our Solutions
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        Tailored solutions for every type of educational institution
                    </p>
                </div>
            </section>

            {/* Solutions Grid */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-8">
                        {solutions.map((solution, idx) => (
                            <div
                                key={idx}
                                data-aos="fade-up"
                                data-aos-delay={idx * 100}
                                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-gray-100"
                            >
                                <div className={`inline-flex p-6 rounded-2xl bg-gradient-to-r ${solution.color} text-white mb-6`}>
                                    {solution.icon}
                                </div>
                                <h3 className="text-3xl font-bold text-gray-900 mb-4">{solution.title}</h3>
                                <p className="text-gray-600 leading-relaxed mb-6 text-lg">{solution.description}</p>
                                <ul className="space-y-3 mb-6">
                                    {solution.features.map((feature, i) => (
                                        <li key={i} className="flex items-center text-gray-700">
                                            <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0 text-xl" />
                                            <span className="text-base font-medium">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/contact">
                                    <button className={`w-full py-3 rounded-xl font-bold text-lg transition-all bg-gradient-to-r ${solution.color} text-white hover:shadow-lg transform hover:scale-105`}>
                                        Learn More <FaArrowRight className="inline ml-2" />
                                    </button>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                <div className="container mx-auto px-4 text-center" data-aos="fade-up">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Not Sure Which Solution Fits You?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                        Our experts will help you choose the perfect solution for your institution. Schedule a free consultation today.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/contact">
                            <button className="px-8 py-4 bg-white text-blue-600 rounded-full text-lg font-bold shadow-2xl hover:shadow-white/50 transform hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto">
                                Schedule Consultation <FaArrowRight />
                            </button>
                        </Link>
                        <Link to="/plans">
                            <button className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full text-lg font-bold hover:bg-white hover:text-blue-600 transform hover:scale-105 transition-all duration-300">
                                View Pricing
                            </button>
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Solutions;

